import React, { useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent,
  Chip, LinearProgress, Divider,
} from '@mui/material';
import { NightsStay, WbSunny, Public, AccessTime } from '@mui/icons-material';

const ECLIPSE_DATA = [
  {
    id: 1, type: 'Total Lunar Eclipse',
    date: new Date(2026, 2, 14),
    time: '06:58 UTC',
    duration: '3h 29m',
    totality: '65m',
    visibility: 'Americas, Europe, Africa',
    description: 'The Moon passes through the center of Earth\'s shadow. The Moon will turn a deep coppery-red color during totality — often called a Blood Moon.',
    magnitude: 1.178,
    penumbra: '06:57 UTC',
    partial: '07:09 UTC',
    total: '07:26 UTC',
    maxEclipse: '07:57 UTC',
    color: '#ef5350',
    icon: '🌕',
  },
  {
    id: 2, type: 'Partial Solar Eclipse',
    date: new Date(2026, 8, 21),
    time: '10:14 UTC',
    duration: '2h 40m',
    visibility: 'Antarctica, Southern Ocean',
    description: 'The Moon partially covers the disk of the Sun. Visible from Antarctica and parts of the Southern Ocean.',
    magnitude: 0.921,
    color: '#ffd600',
    icon: '🌑',
  },
  {
    id: 3, type: 'Total Solar Eclipse',
    date: new Date(2027, 7, 2),
    time: '10:07 UTC',
    duration: '6h 24m',
    totality: '6m 23s',
    visibility: 'Morocco, Algeria, Libya, Egypt, Saudi Arabia, Yemen, Somalia',
    description: 'A spectacular total solar eclipse with a totality of over 6 minutes — one of the longest in the 21st century.',
    magnitude: 1.059,
    color: '#ffd600',
    icon: '🌑',
  },
  {
    id: 4, type: 'Total Lunar Eclipse',
    date: new Date(2026, 7, 28),
    time: '04:33 UTC',
    duration: '3h 55m',
    totality: '90m',
    visibility: 'Pacific Ocean, Americas, Europe',
    description: 'A second total lunar eclipse visible from the Americas and Europe.',
    magnitude: 1.24,
    color: '#ef5350',
    icon: '🌕',
  },
];

function EclipseVisualizer({ eclipse }) {
  if (eclipse.type.includes('Lunar')) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2, display: 'block' }}>
          Eclipse Visualization
        </Typography>
        <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Earth's shadow */}
          <Box sx={{
            width: 120, height: 120, borderRadius: '50%',
            background: 'radial-gradient(circle at 40% 40%, rgba(30,30,50,0.9), rgba(10,15,30,0.95))',
            border: '2px solid rgba(255,255,255,0.2)',
            position: 'relative',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {/* Moon in eclipse (red) */}
            <Box sx={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'radial-gradient(circle at 35% 35%, #ff6b35, #b71c1c)',
              boxShadow: '0 0 20px rgba(183,28,28,0.6)',
            }} />
          </Box>
          {/* Earth (small, off to side) */}
          <Box sx={{
            width: 50, height: 50, borderRadius: '50%',
            background: 'radial-gradient(circle at 35% 35%, #ffffff, #1565c0)',
            ml: 3,
            boxShadow: '0 0 15px rgba(255,255,255,0.4)',
          }} />
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1.5, display: 'block' }}>
          Moon passes through Earth's umbral shadow
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'center', py: 3 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2, display: 'block' }}>
        Eclipse Visualization
      </Typography>
      <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
        {/* Sun */}
        <Box sx={{
          width: 80, height: 80, borderRadius: '50%',
          background: 'radial-gradient(circle at 35% 35%, #fff7c0, #ffd600, #ff8f00)',
          boxShadow: '0 0 30px rgba(255,214,0,0.6)',
          position: 'relative',
        }} />
        {/* Moon covering Sun */}
        <Box sx={{
          width: 76, height: 76, borderRadius: '50%',
          background: 'radial-gradient(circle, #1a1a2e, #000000)',
          border: '2px solid rgba(255,214,0,0.5)',
          position: 'absolute',
          left: 8, top: 2,
          boxShadow: '2px 0 20px rgba(255,214,0,0.8)',
        }} />
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1.5, display: 'block' }}>
        Moon passes between Earth and Sun
      </Typography>
    </Box>
  );
}

export default function EclipsePanel({ events }) {
  const [selected, setSelected] = useState(ECLIPSE_DATA[0]);
  const allEclipses = ECLIPSE_DATA.sort((a, b) => a.date - b.date);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
        🌑 Eclipse Guide
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Upcoming solar and lunar eclipses with visibility maps and timing details
      </Typography>

      <Grid container spacing={2}>
        {/* Eclipse list */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary',
            textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}>
            Upcoming Eclipses
          </Typography>
          {allEclipses.map(eclipse => (
            <Card key={eclipse.id} onClick={() => setSelected(eclipse)}
              sx={{
                mb: 1.5, cursor: 'pointer',
                border: selected.id === eclipse.id
                  ? `1px solid ${eclipse.color}`
                  : `1px solid ${eclipse.color}33`,
                bgcolor: selected.id === eclipse.id ? `${eclipse.color}11` : 'background.paper',
                '&:hover': { border: `1px solid ${eclipse.color}88` },
                transition: 'all 0.2s',
              }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Typography sx={{ fontSize: 24 }}>{eclipse.icon}</Typography>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: eclipse.color }}>
                      {eclipse.type}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {eclipse.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip label={eclipse.date > new Date() ? `In ${Math.ceil((eclipse.date - new Date()) / (1000*60*60*24))} days` : 'Past'} size="small"
                        sx={{ bgcolor: eclipse.date > new Date() ? `${eclipse.color}22` : 'rgba(84,84,84,0.2)',
                          color: eclipse.date > new Date() ? eclipse.color : 'text.secondary',
                          fontSize: 9, height: 16, fontWeight: 700 }} />
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Grid>

        {/* Eclipse detail */}
        <Grid item xs={12} md={8}>
          <Card sx={{ border: `1px solid ${selected.color}44` }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography sx={{ fontSize: 40 }}>{selected.icon}</Typography>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: selected.color }}>
                    {selected.type}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {selected.date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    {' at '}{selected.time}
                  </Typography>
                </Box>
              </Box>

              <EclipseVisualizer eclipse={selected} />

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />

              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.9, mb: 2 }}>
                {selected.description}
              </Typography>

              <Grid container spacing={1.5}>
                {[
                  { icon: <AccessTime />, label: 'Duration',   value: selected.duration,   color: '#ffffff' },
                  { icon: <NightsStay />, label: 'Totality',   value: selected.totality || 'N/A', color: selected.color },
                  { icon: <Public />,     label: 'Visibility', value: selected.visibility.split(',')[0] + '...', color: '#66bb6a' },
                  { icon: <WbSunny />,    label: 'Magnitude',  value: selected.magnitude?.toFixed(3) || 'N/A', color: '#ffa726' },
                ].map(({ icon, label, value, color }) => (
                  <Grid item xs={6} sm={3} key={label}>
                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}11`,
                      border: `1px solid ${color}33`, textAlign: 'center' }}>
                      <Box sx={{ color, mb: 0.5, display: 'flex', justifyContent: 'center' }}>{icon}</Box>
                      <Typography variant="body2" sx={{ fontWeight: 800, color }}>{value}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>{label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {selected.penumbra && (
                <>
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Eclipse Timeline (UTC)
                  </Typography>
                  {[
                    { phase: 'Penumbral begins', time: selected.penumbra },
                    { phase: 'Partial begins',   time: selected.partial },
                    { phase: 'Totality begins',  time: selected.total },
                    { phase: 'Maximum eclipse',  time: selected.maxEclipse },
                  ].filter(t => t.time).map(({ phase, time }) => (
                    <Box key={phase} sx={{ display: 'flex', justifyContent: 'space-between',
                      py: 0.8, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{phase}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700 }}>{time}</Typography>
                    </Box>
                  ))}
                </>
              )}

              <Box sx={{ mt: 2, p: 1.5, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  📍 <strong>Visibility: </strong>{selected.visibility}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}